import { getOptions, scan, setOptions } from 'react-scan';
import App from './App';
import { AppLoading } from '@/shared/ui/app-loading';

import '@mantine/core/styles.css';
/* import/order */
import '@gfazioli/mantine-marquee/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/lightbox/styles.css';
import '@mantine/code-highlight/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/nprogress/styles.css';
import '@mantine/spotlight/styles.css';
import '@gfazioli/mantine-json-tree/styles.css';

import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import '@ungap/custom-elements';
import { DynamicRoute } from '@/app/lib/routing/dynamic-route';
import '@/styles/index.css';
import { API } from '@/shared/config';
import { AXIOS_NO_CACHE_HEADERS } from '@/shared/api/no-cache-headers';
import '@/shared/config/i18n';
import { getRoutes } from '@/app/lib/navigation/get-routes';
import { useHotkeys } from '@mantine/hooks';
import axios from 'axios';

import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import 'reactflow/dist/style.css';
import '@xterm/xterm/css/xterm.css';

import { AppProviders } from './providers';
import { RouteApplicationError } from '@/shared/ui/application-error/route-error';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';

import { queryClientDefaults } from '@/app/config/query-client';
import { handleUnauthorizedError } from '@/features/session-expiration';
import { initSentry } from '@/app/lib/sentry';
import { createPrefetchLoader } from '@/app/lib/prefetch-loader';

import { UnsavedChangesProvider } from '@/shared/lib/hooks/unsaved-changes';
scan({
  enabled: __IS_DEV__,
});
axios.defaults.baseURL = API.PREFIX;
Object.assign(axios.defaults.headers.common, AXIOS_NO_CACHE_HEADERS);

// Конфигурация приходит с бекенда; рендер не ждёт ответа
void initSentry();

const config = {
  defaultOptions: queryClientDefaults,
  queryCache: new QueryCache({
    onError: (error) => handleUnauthorizedError(error, queryClient),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleUnauthorizedError(error, queryClient),
  }),
};
// const queryClientLatest = new QueryClientLatest(config);
const queryClient = new QueryClient(config);

// broadcastQueryClient({
//   queryClient: queryClient as any,
//   broadcastChannel: window.location.hostname,
// });

window.addEventListener('vite:preloadError', (event) => {
  window.location.reload(); // for example, refresh the page
});
const container = document.getElementById('root');
const root = createRoot(container);
const ROUTES = getRoutes({
  selectProperties: ['to', 'public', 'component', 'layout', 'label', 'rules'],
  flat: true,
})
  .filter((x) => x.component)
  .map((route) => {
    return {
      ...route,
      path: route.to,
      element: <DynamicRoute route={route} />,
    };
  });

const ScanWrapper = ({ children }) => {
  useHotkeys([
    [
      'mod + J',
      () => {
        const optionsSignal = getOptions();
        // Access value via .value property
        const options = optionsSignal.value;
        setOptions({ ...options, showToolbar: !options.showToolbar });
      },
    ],
  ]);
  useEffect(() => {
    setOptions({ enabled: false, showToolbar: false });
  }, []);
  return <>{children}</>;
};

export const prefetchLoader = createPrefetchLoader(queryClient);

const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(
  treeMap(
    [
      {
        path: '/',

        element: (
          <ScanWrapper>
            <UnsavedChangesProvider>
              <App />
            </UnsavedChangesProvider>
          </ScanWrapper>
        ),

        errorElement: <RouteApplicationError />,
        children: ROUTES,
      },
    ],
    'children',
    (x) => ({
      ...x,
      shouldRevalidate: ({ currentUrl, nextUrl }) => {
        return currentUrl.pathname !== nextUrl.pathname;
      },
      loader: prefetchLoader,
    }),
  ),
);

root.render(
  <QueryClientProvider client={queryClient}>
    {/*<QueryClientProviderLatest client={queryClientLatest}>*/}
    <ReactQueryDevtools />
    <AppProviders>
      <RouterProvider router={router} fallbackElement={<AppLoading />} />
    </AppProviders>

    {/*</QueryClientProviderLatest>*/}
  </QueryClientProvider>,
);
